import { formatCareSector } from "../Usage/usageOverview.utils";
import { formatFeatureLabel } from "pages/ServiceProvider/SpSetting/AiCreditsUsage/aiUsageLabels";
import {
  colorAt,
  formatPeriodLabel,
  generatePeriodBuckets,
} from "pages/ServiceProvider/SpSetting/AiCreditsUsage/aiUsageChartUtils";

export { formatCareSector };

export const AI_USAGE_ALERT_LABELS = {
  low_balance_high_usage: "Low balance, high usage",
};

export const AI_USAGE_STATUS_LABELS = {
  success: "Success",
  failed: "Failed",
  cached: "Cached",
};

export const AI_USAGE_CARE_SECTOR_OPTIONS = [
  { value: "", label: "All sectors" },
  { value: "ndis", label: "NDIS" },
  { value: "aged_care", label: "Aged care" },
  { value: "both", label: "Both" },
];

export const AI_USAGE_DATE_PRESETS = [
  { id: "mtd", label: "Month to date" },
  { id: "last7", label: "Last 7 days" },
  { id: "last30", label: "Last 30 days" },
  { id: "last90", label: "Last 90 days" },
];

export const AI_USAGE_GRANULARITY_OPTIONS = [
  { id: "day", label: "Daily" },
  { id: "week", label: "Weekly" },
  { id: "month", label: "Monthly" },
];

export const DEFAULT_AI_USAGE_FILTERS = {
  preset: "last30",
  from: "",
  to: "",
  careSector: "",
  granularity: "",
  search: "",
  sort: "spent_aud",
  direction: "desc",
  page: 1,
  perPage: 25,
};

export const diffDaysInclusive = (from, to) => {
  if (!from || !to) {
    return 0;
  }

  const start = new Date(`${from}T12:00:00`);
  const end = new Date(`${to}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 0;
  }

  return Math.floor((end - start) / 86400000) + 1;
};

/** Pick a sensible default bucket size from the selected date range. */
export const suggestAiUsageGranularity = (from, to) => {
  const days = diffDaysInclusive(from, to);
  if (days <= 21) {
    return "day";
  }
  if (days <= 90) {
    return "week";
  }
  return "month";
};

/** Granularities that stay readable for the current range. */
export const availableAiUsageGranularities = (from, to) => {
  const days = diffDaysInclusive(from, to);
  if (days <= 14) {
    return ["day", "week"];
  }
  if (days <= 60) {
    return ["day", "week", "month"];
  }
  if (days <= 180) {
    return ["week", "month"];
  }
  return ["month"];
};

export const resolveAiUsageGranularity = (filters = {}, from, to) => {
  const allowed = availableAiUsageGranularities(from, to);
  const requested = filters.granularity || suggestAiUsageGranularity(from, to);

  if (allowed.includes(requested)) {
    return requested;
  }

  return allowed[allowed.length - 1] || "day";
};

export const aiUsageGranularityLabel = (granularity = "day") => {
  const match = AI_USAGE_GRANULARITY_OPTIONS.find((option) => option.id === granularity);
  return match?.label?.toLowerCase() || "daily";
};

export const aiUsageTrendSubtitle = (granularity = "day") => {
  switch (granularity) {
    case "week":
      return "Weekly AI credit spend and call volume";
    case "month":
      return "Monthly AI credit spend and call volume";
    default:
      return "Daily AI credit spend and call volume";
  }
};

export const AI_USAGE_TENANT_SORT_COLUMNS = [
  { key: "spent_aud", label: "Spend (AUD)" },
  { key: "call_count", label: "Calls" },
  { key: "balance_aud", label: "Balance (AUD)" },
  { key: "name", label: "Tenant" },
];

export const toYmd = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) {
    return "";
  }

  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
};

export const resolveDatePresetRange = (presetId, referenceDate = new Date()) => {
  const ref = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  const to = toYmd(ref);

  if (presetId === "mtd") {
    const fromDate = new Date(ref.getFullYear(), ref.getMonth(), 1);
    return { from: toYmd(fromDate), to };
  }

  const daysByPreset = {
    last7: 6,
    last30: 29,
    last90: 89,
  };
  const offset = daysByPreset[presetId] ?? daysByPreset.last30;
  const fromDate = new Date(ref);
  fromDate.setDate(fromDate.getDate() - offset);

  return { from: toYmd(fromDate), to };
};

export const resolveAiUsageDateRange = (filters = {}, referenceDate = new Date()) => {
  if (filters.from && filters.to) {
    return { from: filters.from, to: filters.to };
  }

  return resolveDatePresetRange(filters.preset || DEFAULT_AI_USAGE_FILTERS.preset, referenceDate);
};

export const createAiUsageFiltersForPreset = (presetId, current = {}) => {
  const nextRange = resolveAiUsageDateRange({
    ...DEFAULT_AI_USAGE_FILTERS,
    preset: presetId,
    from: "",
    to: "",
  });

  return {
    ...current,
    preset: presetId,
    from: "",
    to: "",
    granularity: suggestAiUsageGranularity(nextRange.from, nextRange.to),
  };
};

export const createInitialAiUsageFilters = (overrides = {}) => {
  const preset = overrides.preset || DEFAULT_AI_USAGE_FILTERS.preset;

  return createAiUsageFiltersForPreset(preset, {
    ...DEFAULT_AI_USAGE_FILTERS,
    ...overrides,
  });
};

export const unwrapAiUsageEnvelope = (response) => {
  if (!response) {
    return null;
  }

  if (response?.ai_usage) {
    return response.ai_usage;
  }

  return response?.data?.ai_usage ?? response?.data?.data?.ai_usage ?? null;
};

export const formatAiUsageAud = (value, digits = 2) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return "$0.00";
  }

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(number);
};

export const formatAiUsageCount = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return "0";
  }

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
  }).format(number);
};

export const formatFailedRate = (ratePct, failedCount = 0) => {
  const failed = Number(failedCount);
  if (ratePct === null || ratePct === undefined) {
    return Number.isFinite(failed) && failed > 0
      ? `${formatAiUsageCount(failed)} failed`
      : "0%";
  }

  const number = Number(ratePct);
  if (!Number.isFinite(number)) {
    return "—";
  }

  return `${number.toFixed(1)}%`;
};

export const formatLatencyMs = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) {
    return "—";
  }

  if (number >= 1000) {
    return `${(number / 1000).toFixed(1)}s`;
  }

  return `${Math.round(number)} ms`;
};

export const formatChangePct = (value) => {
  if (value === null || value === undefined) {
    return "—";
  }

  const number = Number(value);
  if (!Number.isFinite(number)) {
    return "—";
  }

  const prefix = number > 0 ? "+" : "";
  return `${prefix}${number.toFixed(1)}%`;
};

export const getChangePctTone = (value) => {
  if (value === null || value === undefined) {
    return "text-gray-500";
  }

  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) {
    return "text-gray-500";
  }

  return number > 0 ? "text-amber-700" : "text-green-700";
};

export const formatAiUsageTimestamp = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

export const formatAiUsageMeta = (meta) => {
  if (!meta?.from || !meta?.to) {
    return "Cross-tenant AI credit spend and call volume";
  }

  const range = `${meta.from} → ${meta.to}`;
  const computed = meta.computed_at
    ? ` · Computed ${formatAiUsageTimestamp(meta.computed_at)}`
    : "";
  const snapshot = meta.snapshot_through
    ? ` · Snapshots through ${meta.snapshot_through}`
    : "";

  return `${range}${computed}${snapshot}`;
};

export const formatAlertSummary = (alert) => {
  if (!alert) {
    return "";
  }

  const typeLabel = AI_USAGE_ALERT_LABELS[alert.type] || alert.type || "Alert";
  const name = alert.name || `Tenant #${alert.sp_id}`;
  const balance = formatAiUsageAud(alert.balance_aud);
  const spent = formatAiUsageAud(alert.spent_7d_aud);

  return `${typeLabel}: ${name} — balance ${balance}, spent (7d) ${spent}`;
};

export const buildTenantTableColumns = () => [
  { key: "name", label: "Tenant", sortable: true },
  { key: "care_sector", label: "Sector", sortable: false },
  { key: "spent_aud", label: "Spend (AUD)", sortable: true, align: "right" },
  { key: "call_count", label: "Calls", sortable: true, align: "right" },
  { key: "balance_aud", label: "Balance (AUD)", sortable: true, align: "right" },
  { key: "ai_enabled", label: "AI", sortable: false, align: "center" },
];

export const buildAiUsageOverviewQuery = (
  filters = {},
  basePath = "super-admin/ai-usage/overview"
) => {
  const params = new URLSearchParams();
  const range = resolveAiUsageDateRange(filters);

  params.append("from", range.from);
  params.append("to", range.to);

  if (filters.careSector) {
    params.append("care_sector", filters.careSector);
  }

  const granularity = resolveAiUsageGranularity(filters, range.from, range.to);
  params.append("granularity", granularity);

  const query = params.toString();
  return query ? `${basePath}?${query}` : basePath;
};

export const buildAiUsageTenantsQuery = (
  filters = {},
  basePath = "super-admin/ai-usage/tenants"
) => {
  const params = new URLSearchParams();
  const range = resolveAiUsageDateRange(filters);

  params.append("from", range.from);
  params.append("to", range.to);

  if (filters.search) {
    params.append("search", filters.search);
  }

  if (filters.careSector) {
    params.append("care_sector", filters.careSector);
  }

  if (filters.sort) {
    params.append("sort", filters.sort);
  }

  if (filters.direction) {
    params.append("direction", filters.direction);
  }

  params.append("page", String(filters.page || 1));
  params.append("per_page", String(filters.perPage || 25));

  const query = params.toString();
  return query ? `${basePath}?${query}` : basePath;
};

export const buildAiUsageTenantDetailQuery = (
  filters = {},
  tenantId,
  basePath = "super-admin/ai-usage/tenants"
) => {
  const params = new URLSearchParams();
  const range = resolveAiUsageDateRange(filters);

  params.append("from", range.from);
  params.append("to", range.to);

  const granularity = resolveAiUsageGranularity(filters, range.from, range.to);
  params.append("granularity", granularity);

  const query = params.toString();
  const path = `${basePath}/${tenantId}`;
  return query ? `${path}?${query}` : path;
};

export const normalizeTenantSort = (sort, direction = "desc") => {
  const allowed = AI_USAGE_TENANT_SORT_COLUMNS.map((column) => column.key);
  const nextSort = allowed.includes(sort) ? sort : "spent_aud";
  const nextDirection = direction === "asc" ? "asc" : "desc";

  return { sort: nextSort, direction: nextDirection };
};

/** AiUsageService returns { items }; overview API returns a bare array. */
export const normalizeByFeatureItems = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  return Array.isArray(payload?.items) ? payload.items : [];
};

/** AiUsageService returns { series }; overview API returns a bare array. */
export const normalizeCostSeries = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  return Array.isArray(payload?.series) ? payload.series : [];
};

export const normalizeByUserRows = (rows = []) =>
  rows.map((row) => ({
    ...row,
    name: row.name || row.user_name,
    cost_aud: row.cost_aud ?? row.spent_aud,
  }));

export const mapByFeatureForChart = (rows = []) =>
  normalizeByFeatureItems(rows).map((row) => ({
    ...row,
    feature_key: row.feature_key || row.feature,
    label: formatFeatureLabel(row),
  }));

export const mapByModelForChart = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    model: row.model || "unknown",
    label: row.model || "unknown",
  }));

export const mapByStatusForChart = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    const statusKey = row.status_key || row.status || "success";

    return {
      ...row,
      status_key: statusKey,
      label: AI_USAGE_STATUS_LABELS[statusKey] || statusKey,
    };
  });

export const mapBySectorForChart = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    sector_key: row.sector_key || row.care_sector || "unknown",
    label: formatCareSector(row.sector_key || row.care_sector) || "Unknown",
  }));

export const STATUS_TREND_COLORS = {
  success: "#22c55e",
  failed: "#ef4444",
  cached: "#6366f1",
};

/** Pivot long-format trend rows into stacked-bar chart data (top N + Other). */
export const pivotBreakdownTrends = ({
  trends = [],
  keyField,
  from,
  to,
  granularity = "day",
  topN = 5,
  valueKey = "call_count",
  labelFn = (key) => key,
  colorFn,
}) => {
  if (!from || !to || !keyField) {
    return { chartData: [], keys: [] };
  }

  const totalsByKey = new Map();
  trends.forEach((row) => {
    const key = String(row[keyField] ?? "unknown");
    totalsByKey.set(key, (totalsByKey.get(key) || 0) + (Number(row[valueKey]) || 0));
  });

  const topKeys = [...totalsByKey.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([key]) => key);

  const topSet = new Set(topKeys);
  const periods = generatePeriodBuckets(from, to, granularity);
  const byPeriod = new Map();

  periods.forEach((period) => {
    const entry = { period };
    topKeys.forEach((key) => {
      entry[key] = 0;
    });
    entry._other = 0;
    byPeriod.set(period, entry);
  });

  trends.forEach((row) => {
    const period = String(row.period);
    if (!byPeriod.has(period)) {
      return;
    }

    const value = Number(row[valueKey]) || 0;
    const key = String(row[keyField] ?? "unknown");
    const bucket = byPeriod.get(period);

    if (topSet.has(key)) {
      bucket[key] += value;
    } else {
      bucket._other += value;
    }
  });

  const seriesKeys = [...topKeys];
  if ([...byPeriod.values()].some((row) => row._other > 0)) {
    seriesKeys.push("_other");
  }

  const keys = seriesKeys.map((id, index) => ({
    id,
    dataKey: id,
    label: id === "_other" ? "Other" : labelFn(id),
    color:
      id === "_other"
        ? "#94a3b8"
        : colorFn?.(id, index) ?? colorAt(index),
  }));

  const chartData = periods.map((period) => {
    const row = byPeriod.get(period) || {};
    return {
      period,
      label: formatPeriodLabel(period, granularity, {
        shortMonth: granularity === "month",
      }),
      ...Object.fromEntries(seriesKeys.map((key) => [key, row[key] || 0])),
      total: seriesKeys.reduce((sum, key) => sum + (row[key] || 0), 0),
    };
  });

  return { chartData, keys };
};

export const buildAiUsageDashboardChips = (aiUsage = {}) => [
  {
    id: "mtd_spent_aud",
    label: "MTD AI spend",
    value: formatAiUsageAud(aiUsage.mtd_spent_aud),
    to: "/ai-usage",
    title: "Month-to-date AI credit spend across tenants",
  },
  {
    id: "active_tenant_count",
    label: "Active tenants",
    value: formatAiUsageCount(aiUsage.active_tenant_count),
    to: "/ai-usage",
    title: "Tenants with AI usage this month",
  },
  {
    id: "top_tenant",
    label: "Top spender",
    value: aiUsage.top_tenant?.name || "—",
    to: aiUsage.top_tenant?.sp_id ? `/tenants/${aiUsage.top_tenant.sp_id}?tab=ai-usage` : "/ai-usage",
    title: aiUsage.top_tenant?.spent_aud
      ? `${aiUsage.top_tenant.name} — ${formatAiUsageAud(aiUsage.top_tenant.spent_aud)} MTD`
      : "Highest AI spend this month",
  },
];
